import { useTranslation } from 'react-i18next';
import { AppShell } from '@/components/layout/AppShell';
import { PageContainer } from '@/components/layout/PageContainer';
import { TopBar } from '@/components/layout/TopBar';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { ListItem } from '@/components/ui/ListItem';
import { Spinner } from '@/components/ui/Spinner';
import {
  useCompany,
  useEmployees,
  useTopupEmployee,
  useVerifyEmployee,
} from '@/api/queries/employer';
import { useMe } from '@/api/queries/user';
import { BalanceFormatter } from '@/components/domain/BalanceFormatter';
import { Navigate } from 'react-router-dom';
import { paths } from '@/routes/paths';
import { useUIStore } from '@/store/ui';
import { useState } from 'react';
import { BottomSheet } from '@/components/ui/BottomSheet';
import { Input } from '@/components/ui/Input';
import { dollarsInputToCents } from '@/utils/money';

export function BusinessCabinetPage(): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const company = useCompany();
  const employees = useEmployees();
  const verify = useVerifyEmployee();
  const topup = useTopupEmployee();
  const pushToast = useUIStore((s) => s.pushToast);

  const [topupId, setTopupId] = useState<string | null>(null);
  const [topupAmount, setTopupAmount] = useState('100');

  if (me.isLoading || company.isLoading) {
    return (
      <AppShell>
        <PageContainer><Spinner /></PageContainer>
      </AppShell>
    );
  }

  if (me.data?.role !== 'employer_admin') {
    return <Navigate to={paths.business} replace />;
  }

  const doTopup = async () => {
    if (!topupId) return;
    const cents = dollarsInputToCents(topupAmount);
    if (!cents || cents < 100) {
      pushToast({ body: t('errors.validation'), variant: 'error' });
      return;
    }
    try {
      await topup.mutateAsync({ id: topupId, amountCents: cents });
      pushToast({ body: t('common.done'), variant: 'success' });
      setTopupId(null);
    } catch {
      pushToast({ body: t('errors.generic'), variant: 'error' });
    }
  };

  return (
    <AppShell>
      <PageContainer>
        <TopBar back title={t('business.active_title')} bell />
        {company.data && (
          <Card variant="light" padding="md">
            <div style={{ fontWeight: 700, fontSize: 18 }}>{company.data.legal_name}</div>
            <div style={{ color: 'var(--color-text-on-light-muted)', marginTop: 4 }}>
              {company.data.country_code} · {company.data.verification_status}
            </div>
          </Card>
        )}
        <Card variant="light" padding="md">
          <div style={{ fontWeight: 600, marginBottom: 12 }}>Employees</div>
          {employees.isLoading && <Spinner />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {employees.data?.items?.map((e) => (
              <ListItem
                key={e.id}
                title={`${e.given_name ?? ''} ${e.surname ?? ''} (${e.email})`}
                subtitle={`Status: ${e.status} · Wallet `}
                trailing={
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <BalanceFormatter cents={e.wallet_balance_cents} />
                    {e.status === 'pending' && (
                      <Button size="sm" onClick={() => verify.mutate(e.id)}>{t('common.confirm')}</Button>
                    )}
                    <Button size="sm" variant="secondary" onClick={() => setTopupId(e.id)}>
                      Top-up
                    </Button>
                  </div>
                }
              />
            ))}
          </div>
        </Card>

        <BottomSheet open={topupId !== null} onClose={() => setTopupId(null)} title="Employee top-up">
          <Input
            label={t('wallet.topup.amount_label')}
            inputMode="decimal"
            value={topupAmount}
            onChange={(e) => setTopupAmount(e.target.value)}
          />
          <Button block onClick={doTopup} loading={topup.isPending}>
            {t('wallet.topup.submit')}
          </Button>
        </BottomSheet>
      </PageContainer>
    </AppShell>
  );
}
